import { FC, useMemo } from 'react';
import { Project } from "@brg/api";
import { FlexBox } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { ProjectDelegate } from 'project-delegate';
import { AlertIcon } from 'shared/icons/AlertIcon';
import styled from 'styled-components';
import { Colors } from '@brg/ui';
import { LayoutConst } from 'core/modules/layout.module';
import { BLOCKING_REASONS } from './ApprovalDialog';

const BlockedProjectBannerWrapper = styled(FlexBox)`
  width: 100%;
  padding: 16px 20px;
  background-color: ${p => p.theme.mainDropdownStyles.bg};
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  border-radius: 8px;

  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
  }
`;

const AlertIconWrapper = styled(FlexBox)`
  flex-shrink: 0;
  margin-top: 2px;
`;

const ContentWrapper = styled(FlexBox)`
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const MainText = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: ${p => p.theme.mainSearchBar.tabs.color};

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReasonItem = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${p => p.theme.mainSearchBar.tabs.color};
  padding-left: 12px;
  position: relative;
  
  &::before {
    content: "-";
    position: absolute;
  color: ${p => p.theme.mainSearchBar.tabs.color};
    left: 0;
    font-weight: bold;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

const BlockedProjectContainer = styled.div`

  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 0 10px;
  }
`;

interface BlockedProjectBannerProps {
  project: Project;
  isTemporaryBlocked?: boolean;
  temporaryBlockingReasons?: number[];
  temporaryOtherReason?: string;
}

export const BlockedProjectBanner: FC<BlockedProjectBannerProps> = ({ 
  project, 
  isTemporaryBlocked, 
  temporaryBlockingReasons, 
  temporaryOtherReason 
}) => {
  const { t } = useTranslation();
  
  const isBlockedProject = useMemo(() => {
    if (isTemporaryBlocked && temporaryBlockingReasons && temporaryBlockingReasons.length > 0) {
      return true;
    }
    
    const projectAny = project as any;
    if (projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons) {
      const filteredReasons = projectAny.lastBannedInfo.bannedReasonInfo.reasons.filter((r: number) => r !== 13);
      if (filteredReasons.length > 0) {
        return true;
      }
    }
    
    return project && ProjectDelegate.isRejectedProject(project);
  }, [project, isTemporaryBlocked, temporaryBlockingReasons, temporaryOtherReason]);


  const translatedReasons = useMemo(() => {
    if (isTemporaryBlocked && temporaryBlockingReasons) {
      const filteredReasons = temporaryBlockingReasons.filter(r => r !== 13);
      
      const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
        const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
        return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
      });
      
      if (temporaryOtherReason && temporaryBlockingReasons.includes(12)) {
        const otherIndex = translatedReasonsList.findIndex((text: string) => 
          text === t('project_edit.blocking_reason_other')
        );
        if (otherIndex !== -1) {
          translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${temporaryOtherReason}`;
        }
      }
      
      return translatedReasonsList;
    }
    
    const projectAny = project as any;
    if (projectAny?.lastBannedInfo?.bannedReasonInfo) {
      const { reasons, otherReason } = projectAny.lastBannedInfo.bannedReasonInfo;
      
      if (reasons && Array.isArray(reasons)) {
        const filteredReasons = reasons.filter((r: number) => r !== 13);
        
        const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
          const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
          return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
        });
        
        if (otherReason && reasons.includes(12)) {
          const otherIndex = translatedReasonsList.findIndex(text => 
            text === t('project_edit.blocking_reason_other')
          );
          if (otherIndex !== -1) {
            translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${otherReason}`;
          }
        }
        
        return translatedReasonsList;
      }
    }
    
    if (!project?.approvals) {
      return [];
    }
    
    const bannedApproval = project.approvals.find((approval: any) => 
      approval.contextService === 'banned'
    );
    
    if (!bannedApproval?.data) {
      return [];
    }
    
    try {
      const bannedData = typeof bannedApproval.data === 'string' 
        ? JSON.parse(bannedApproval.data) 
        : bannedApproval.data;
      
      const { reasons, otherReason } = bannedData as { reasons: number[], otherReason?: string };
      
      if (!reasons || !Array.isArray(reasons)) {
        return [];
      }
      
      const filteredReasons = reasons.filter((r: number) => r !== 13);
      
      const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
        const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
        return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
      });
      
      if (otherReason && reasons.includes(12)) {
        const otherIndex = translatedReasonsList.findIndex(text => 
          text === t('project_edit.blocking_reason_other')
        );
        if (otherIndex !== -1) {
          translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${otherReason}`;
        }
      }
      
      return translatedReasonsList;
    } catch (error) {
      console.error('Error parsing blocked project data:', error);
      return [];
    }
  }, [project, t, isTemporaryBlocked, temporaryBlockingReasons, temporaryOtherReason]);

  if (!isBlockedProject) {
    return null;
  }

  const hasUnbanRequest = project?.contexts?.includes('unbanrequest');

  if (!translatedReasons || translatedReasons.length === 0) {
    return null;
  }

  return (
    <BlockedProjectContainer>
    <BlockedProjectBannerWrapper>
      <AlertIconWrapper>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 0C4.47748 0 0 4.47748 0 10C0 15.5232 4.47748 20 10 20C15.5232 20 20 15.5238 20 10C20 4.47748 15.5231 0 10 0ZM10 18.125C5.51253 18.125 1.87503 14.4875 1.87503 10C1.87503 5.51253 5.51253 1.87503 10 1.87503C14.4875 1.87503 18.125 5.51253 18.125 10C18.125 14.4875 14.4875 18.125 10 18.125ZM10.0006 4.38561C9.2844 4.38561 8.74248 4.75938 8.74248 5.36311V10.9006C8.74248 11.505 9.28433 11.8775 10.0006 11.8775C10.6994 11.8775 11.2588 11.4894 11.2588 10.9006V5.36311C11.2587 4.77371 10.6994 4.38561 10.0006 4.38561ZM10.0006 13.125C9.31251 13.125 8.75247 13.685 8.75247 14.3737C8.75247 15.0612 9.31251 15.6212 10.0006 15.6212C10.6888 15.6212 11.2482 15.0612 11.2482 14.3737C11.2481 13.685 10.6888 13.125 10.0006 13.125Z" fill="#FD6C52"/>
      </svg>
      </AlertIconWrapper>
      <ContentWrapper>
        <MainText>
          {t('project_edit.blocked_project_banner_title')}
        </MainText>
        {hasUnbanRequest ? (
          <ReasonsList>
            <ReasonItem>
              {t('project_edit.project_under_review')}
            </ReasonItem>
          </ReasonsList>
        ) : (
          <ReasonsList>
            {translatedReasons.map((reason: string, index: number) => (
              <ReasonItem key={index}>
                {reason}
              </ReasonItem>
            ))}
          </ReasonsList>
        )}
      </ContentWrapper>
    </BlockedProjectBannerWrapper>

    </BlockedProjectContainer>
  );
};
import { FC, useMemo } from 'react';
import { Project } from "@brg/api";
import { FlexBox } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { ProjectDelegate } from 'project-delegate';
import { AlertIcon } from 'shared/icons/AlertIcon';
import styled from 'styled-components';
import { Colors } from '@brg/ui';
import { LayoutConst } from 'core/modules/layout.module';
import { BLOCKING_REASONS } from './ApprovalDialog';

const BlockedProjectBannerWrapper = styled(FlexBox)`
  width: 100%;
  padding: 16px 20px;
  background-color: ${p => p.theme.mainDropdownStyles.bg};
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  border-radius: 8px;

  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
  }
`;

const AlertIconWrapper = styled(FlexBox)`
  flex-shrink: 0;
  margin-top: 2px;
`;

const ContentWrapper = styled(FlexBox)`
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const MainText = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: ${p => p.theme.mainSearchBar.tabs.color};

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReasonItem = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${p => p.theme.mainSearchBar.tabs.color};
  padding-left: 12px;
  position: relative;
  
  &::before {
    content: "-";
    position: absolute;
  color: ${p => p.theme.mainSearchBar.tabs.color};
    left: 0;
    font-weight: bold;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`;

const BlockedProjectContainer = styled.div`

  @media (max-width: ${LayoutConst.screen.m}px) {
    padding: 0 10px;
  }
`;

interface BlockedProjectBannerProps {
  project: Project;
  isTemporaryBlocked?: boolean;
  temporaryBlockingReasons?: number[];
  temporaryOtherReason?: string;
}

export const BlockedProjectBanner: FC<BlockedProjectBannerProps> = ({ 
  project, 
  isTemporaryBlocked, 
  temporaryBlockingReasons, 
  temporaryOtherReason 
}) => {
  const { t } = useTranslation();
  
  const isBlockedProject = useMemo(() => {
    if (isTemporaryBlocked && temporaryBlockingReasons && temporaryBlockingReasons.length > 0) {
      return true;
    }
    
    const projectAny = project as any;
    if (projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons) {
      const filteredReasons = projectAny.lastBannedInfo.bannedReasonInfo.reasons.filter((r: number) => r !== 13);
      if (filteredReasons.length > 0) {
        return true;
      }
    }
    
    return project && ProjectDelegate.isRejectedProject(project);
  }, [project, isTemporaryBlocked, temporaryBlockingReasons, temporaryOtherReason]);


  const translatedReasons = useMemo(() => {
    if (isTemporaryBlocked && temporaryBlockingReasons) {
      const filteredReasons = temporaryBlockingReasons.filter(r => r !== 13);
      
      const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
        const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
        return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
      });
      
      if (temporaryOtherReason && temporaryBlockingReasons.includes(12)) {
        const otherIndex = translatedReasonsList.findIndex((text: string) => 
          text === t('project_edit.blocking_reason_other')
        );
        if (otherIndex !== -1) {
          translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${temporaryOtherReason}`;
        }
      }
      
      return translatedReasonsList;
    }
    
    const projectAny = project as any;
    if (projectAny?.lastBannedInfo?.bannedReasonInfo) {
      const { reasons, otherReason } = projectAny.lastBannedInfo.bannedReasonInfo;
      
      if (reasons && Array.isArray(reasons)) {
        const filteredReasons = reasons.filter((r: number) => r !== 13);
        
        const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
          const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
          return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
        });
        
        if (otherReason && reasons.includes(12)) {
          const otherIndex = translatedReasonsList.findIndex(text => 
            text === t('project_edit.blocking_reason_other')
          );
          if (otherIndex !== -1) {
            translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${otherReason}`;
          }
        }
        
        return translatedReasonsList;
      }
    }
    
    if (!project?.approvals) {
      return [];
    }
    
    const bannedApproval = project.approvals.find((approval: any) => 
      approval.contextService === 'banned'
    );
    
    if (!bannedApproval?.data) {
      return [];
    }
    
    try {
      const bannedData = typeof bannedApproval.data === 'string' 
        ? JSON.parse(bannedApproval.data) 
        : bannedApproval.data;
      
      const { reasons, otherReason } = bannedData as { reasons: number[], otherReason?: string };
      
      if (!reasons || !Array.isArray(reasons)) {
        return [];
      }
      
      const filteredReasons = reasons.filter((r: number) => r !== 13);
      
      const translatedReasonsList = filteredReasons.map((reasonEnum: number) => {
        const reasonConfig = BLOCKING_REASONS.find(r => r.value === reasonEnum);
        return reasonConfig ? t(reasonConfig.labelKey) : `Unknown reason (${reasonEnum})`;
      });
      
      if (otherReason && reasons.includes(12)) {
        const otherIndex = translatedReasonsList.findIndex(text => 
          text === t('project_edit.blocking_reason_other')
        );
        if (otherIndex !== -1) {
          translatedReasonsList[otherIndex] = `${t('project_edit.blocking_reason_other')}: ${otherReason}`;
        }
      }
      
      return translatedReasonsList;
    } catch (error) {
      console.error('Error parsing blocked project data:', error);
      return [];
    }
  }, [project, t, isTemporaryBlocked, temporaryBlockingReasons, temporaryOtherReason]);

  if (!isBlockedProject) {
    return null;
  }

  const hasUnbanRequest = project?.contexts?.includes('unbanrequest');

  if (!translatedReasons || translatedReasons.length === 0) {
    return null;
  }

  return (
    <BlockedProjectContainer>
    <BlockedProjectBannerWrapper>
      <AlertIconWrapper>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 0C4.47748 0 0 4.47748 0 10C0 15.5232 4.47748 20 10 20C15.5232 20 20 15.5238 20 10C20 4.47748 15.5231 0 10 0ZM10 18.125C5.51253 18.125 1.87503 14.4875 1.87503 10C1.87503 5.51253 5.51253 1.87503 10 1.87503C14.4875 1.87503 18.125 5.51253 18.125 10C18.125 14.4875 14.4875 18.125 10 18.125ZM10.0006 4.38561C9.2844 4.38561 8.74248 4.75938 8.74248 5.36311V10.9006C8.74248 11.505 9.28433 11.8775 10.0006 11.8775C10.6994 11.8775 11.2588 11.4894 11.2588 10.9006V5.36311C11.2587 4.77371 10.6994 4.38561 10.0006 4.38561ZM10.0006 13.125C9.31251 13.125 8.75247 13.685 8.75247 14.3737C8.75247 15.0612 9.31251 15.6212 10.0006 15.6212C10.6888 15.6212 11.2482 15.0612 11.2482 14.3737C11.2481 13.685 10.6888 13.125 10.0006 13.125Z" fill="#FD6C52"/>
      </svg>
      </AlertIconWrapper>
      <ContentWrapper>
        <MainText>
          {t('project_edit.blocked_project_banner_title')}
        </MainText>
        {hasUnbanRequest ? (
          <ReasonsList>
            <ReasonItem>
              {t('project_edit.project_under_review')}
            </ReasonItem>
          </ReasonsList>
        ) : (
          <ReasonsList>
            {translatedReasons.map((reason: string, index: number) => (
              <ReasonItem key={index}>
                {reason}
              </ReasonItem>
            ))}
          </ReasonsList>
        )}
      </ContentWrapper>
    </BlockedProjectBannerWrapper>

    </BlockedProjectContainer>
  );
};
